import React, { useEffect, useState } from 'react';
import { Alert, Button, Container, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import EventForm from '../components/events/EventForm';
import EventDetails from '../components/events/EventDetails';
import EventParticipantsPanel from '../components/events/EventParticipantsPanel';
import EventTaskLinksDialog from '../components/events/EventTaskLinksDialog';
import { LoadingIndicator } from '../components/assets';
import {
  createEvent,
  getEvent,
  removeEventImage,
  replaceEvent,
  uploadEventImage,
} from '../utils/api';

export default function EventEditor() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(!!eventId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tasksOpen, setTasksOpen] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;
    let alive = true;
    setLoading(true);
    getEvent(eventId)
      .then((loaded) => { if (alive) setEvent(loaded); })
      .catch((loadError) => {
        if (alive) {
          setError(loadError.data?.message || loadError.message || 'Unable to load the event.');
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eventId]);

  const save = async (payload, imageChange = {}) => {
    setSaving(true);
    setError('');
    let savedEvent = null;
    try {
      if (eventId) {
        savedEvent = await replaceEvent(eventId, payload, event.revision);
      } else {
        savedEvent = await createEvent(payload);
      }
      setEvent(savedEvent);
      if (imageChange.file) {
        savedEvent = await uploadEventImage(savedEvent.id, imageChange.file);
        setEvent(savedEvent);
      } else if (imageChange.remove) {
        savedEvent = await removeEventImage(savedEvent.id);
        setEvent(savedEvent);
      }
      navigate('/events');
    } catch (saveError) {
      setError(saveError.data?.message || saveError.message || 'Unable to save the event.');
      if (!eventId && savedEvent?.id) {
        navigate(`/event/${savedEvent.id}`, { replace: true });
      }
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 18 }}>
      {error && <Alert severity="error" sx={{ mb: 2, color: 'secondary.main' }}>{error}</Alert>}
      {loading ? <LoadingIndicator text="loading event" /> : (
        eventId && !event ? (
          <Stack alignItems="flex-start">
            <Button variant="outlined" onClick={() => navigate('/events')}>
              Back to events
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {event?.permissions?.can_edit === false ? (
              <EventDetails event={event} />
            ) : (
              <EventForm
                key={event?.id || 'new-event'}
                event={event}
                saving={saving}
                onSubmit={save}
                onCancel={() => navigate('/events')}
                onManageTasks={() => setTasksOpen(true)}
              />
            )}
            {eventId && event && (
              <EventParticipantsPanel
                event={event}
                onLeft={() => navigate('/events')}
              />
            )}
          </Stack>
        )
      )}
      <EventTaskLinksDialog
        event={event}
        open={tasksOpen}
        onClose={() => setTasksOpen(false)}
      />
    </Container>
  );
}
